import React, { useState, useMemo, useEffect } from 'react';
import { string, func, oneOfType, instanceOf, bool } from 'prop-types';
import { clearClassName } from 'utils/string';
import DatePicker from 'react-datepicker';
import usePortal from 'components/UI/utils/usePortal';
import classnames from 'classnames';
import moment from 'moment';
import { toStartOfDay } from './utils';

import './DatePicker.scss';

const cbn = 'form-date-picker';

const toStringDate = (date) => {
  if (date && date.format) {
    return date.format('YYYY-MM-DD');
  }
  const dt = date.split('T');
  return dt[0];
};

export function toJSDate(date) {
  if (!date || !moment(date).isValid()) {
    return null;
  }

  const dt = moment(toStringDate(date), ['YYYY-MM-DD']);
  return new Date(dt.year(), dt.month(), dt.date());
}

const getOpenToDate = (value, minDate) => {
  if (value) {
    return value;
  }

  const min = toJSDate(minDate);
  const current = new Date();

  return min && min.getTime() > current.getTime() ? min : current;
};

function FormDatePicker({
  dateFormat,
  disabled,
  error,
  hasOffset,
  isClearable,
  maxDate,
  minDate,
  moveLeftPickerPosition,
  name,
  onBlur,
  onCalendarClose,
  onCalendarOpen,
  onChange,
  onFocus,
  open,
  placeholder,
  readOnly,
  value,
  weekNumbers,
  showMonthYearPicker,
  showTimeSelect,
  inputRef = null,
}) {
  const [localValue, setLocalValue] = useState(() => toJSDate(value));
  const openToDate = useMemo(() => getOpenToDate(localValue, minDate), [
    localValue,
    minDate,
  ]);

  const Portal = usePortal();

  useEffect(() => {
    setLocalValue(toJSDate(value));
  }, [value]);

  const applyValue = React.useCallback(
    (date) => {
      const newTextValue = toStartOfDay(date);
      const textValue = toStartOfDay(value);

      if (newTextValue !== textValue) {
        onChange(newTextValue);
      }
    },
    [onChange, value],
  );

  const onDateChange = React.useCallback(
    (date) => {
      setLocalValue(date);
      applyValue(date);
    },
    [setLocalValue, applyValue],
  );

  if (!localValue && readOnly) {
    return <div className={classnames(cbn, `${cbn}--display-only`)}>N/A</div>;
  }

  return (
    <DatePicker
      className={classnames(clearClassName(`${cbn}--${name}`), cbn, {
        [`${cbn}--is-clearable`]: isClearable,
        [`${cbn}--display-only`]: readOnly,
        [`${cbn}--has-offset`]: hasOffset,
        [`${cbn}--has-error`]: error,
        'move-position': moveLeftPickerPosition,
      })}
      minDate={toJSDate(minDate)}
      maxDate={toJSDate(maxDate)}
      selected={localValue}
      openToDate={openToDate}
      name={name}
      dateFormat={dateFormat}
      dateFormatCalendar={dateFormat}
      disabledKeyboardNavigation
      readOnly={disabled || readOnly}
      disabled={disabled || readOnly}
      isClearable={isClearable && !readOnly && !disabled}
      onMonthChange={onDateChange}
      onYearChange={onDateChange}
      onChange={onDateChange}
      onFocus={onFocus}
      onBlur={onBlur}
      placeholderText={disabled || readOnly ? '' : placeholder}
      showWeekNumbers={weekNumbers}
      showMonthDropdown
      showYearDropdown
      showMonthYearPicker={showMonthYearPicker}
      scrollableYearDropdown
      onClickOutside={() => applyValue(localValue)}
      onCalendarClose={onCalendarClose}
      onCalendarOpen={onCalendarOpen}
      open={open}
      dropdownMode="select"
      popperPlacement="bottom"
      autoComplete="off"
      popperContainer={Portal}
      popperClassName="form-date-picker__popper"
      popperModifiers={{
        preventOverflow: {
          enabled: true,
          escapeWithReference: false,
          boundariesElement: 'viewport',
        },
      }}
      showTimeSelect={showTimeSelect}
      ref={inputRef ?? null}
    />
  );
}

FormDatePicker.propTypes = {
  dateFormat: string,
  disabled: bool,
  error: string,
  hasOffset: bool,
  isClearable: bool,
  maxDate: oneOfType([string, instanceOf(moment)]),
  minDate: oneOfType([string, instanceOf(moment)]),
  moveLeftPickerPosition: bool,
  name: string,
  onBlur: func,
  onCalendarClose: func,
  onCalendarOpen: func,
  onChange: func,
  onFocus: func,
  open: bool,
  placeholder: string,
  readOnly: bool,
  value: oneOfType([string, instanceOf(moment)]),
  weekNumbers: bool,
  showMonthYearPicker: bool,
  showTimeSelect: bool,
};

FormDatePicker.defaultProps = {
  dateFormat: 'MMM d, yyyy',
  disabled: false,
  error: null,
  hasOffset: false,
  isClearable: true,
  maxDate: null,
  minDate: null,
  moveLeftPickerPosition: false,
  name: undefined,
  onBlur: () => {},
  onCalendarClose: undefined,
  onCalendarOpen: undefined,
  onChange: () => {},
  onFocus: () => {},
  open: undefined,
  placeholder: 'mmm dd, yyyy',
  readOnly: false,
  value: null,
  weekNumbers: false,
  showMonthYearPicker: false,
  showTimeSelect: false,
};

export default FormDatePicker;
